export type Role =
  | "community_member"
  | "trusted_verifier"
  | "organization_admin"
  | "platform_admin";

export interface AuthedUser {
  id: number;
  clerk_user_id: string;
  email: string;
  full_name: string | null;
  role: Role;
  organization_id: number | null;
  city: string | null;
  neighborhood: string | null;
  is_active: number;
}

export interface AdminUserRow {
  id: number;
  email: string;
  full_name: string | null;
  role: Role;
  organization_id: number | null;
  organization_name: string | null;
  city: string | null;
  neighborhood: string | null;
  is_active: number;
  created_at: string;
  last_seen_at?: string | null;
  invite_status?: "pending" | "accepted" | "revoked" | null;
}

export type NotificationEventType =
  | "invite_created"
  | "report_submitted"
  | "report_approved"
  | "report_rejected"
  | "more_info_requested"
  | "alert_published";

export interface NotificationRow {
  id: number;
  event_type: NotificationEventType;
  payload_json: string;
  read_at: string | null;
  created_at: string;
}

export interface InviteRow {
  id: number;
  email: string;
  role: Role;
  organization_id: number | null;
  organization_name: string | null;
  status: "pending" | "accepted" | "revoked";
  created_at: string;
  accepted_at: string | null;
  invited_by_user_id: number | null;
  invited_by_email: string | null;
  invited_by_name: string | null;
}

export interface AdminOrganization extends Organization {
  is_active: number;
  member_count: number;
  verifier_count: number;
  report_count: number;
  last_report_at: string | null;
  created_at: string;
}

export interface ReportActivity {
  id: number;
  report_id: number;
  actor_user_id: number | null;
  actor_name: string | null;
  actor_email: string | null;
  actor_role: Role | null;
  action:
    | "status_changed"
    | "assigned_verifier"
    | "unassigned_verifier"
    | "verification_note_added"
    | "alert_published"
    | "admin_note_updated";
  previous_status: string | null;
  new_status: string | null;
  note: string | null;
  created_at: string;
}

export interface Alert {
  id: number;
  title: string;
  category: string;
  neighborhood: string | null;
  urgency: "low" | "medium" | "high" | "critical";
  public_summary: string;
  recommended_action: string | null;
  status: "active" | "archived" | "resolved";
  updated_at: string;
}

export type ReportStatus =
  | "submitted"
  | "needs_verification"
  | "more_info_needed"
  | "approved"
  | "rejected"
  | "resolved";

export interface Report {
  id: number;
  report_type: string;
  title?: string | null;
  location: string | null;
  neighborhood: string | null;
  incident_datetime: string | null;
  description: string;
  urgency: "low" | "medium" | "high" | "critical";
  anonymous: number;
  status: ReportStatus;
  assigned_verifier_id?: number | null;
  created_at: string;
  updated_at?: string;
  submitter_name?: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
  admin_notes?: string | null;
}

export interface MyReport {
  id: number;
  report_type: string;
  title: string | null;
  status: ReportStatus;
  urgency: "low" | "medium" | "high" | "critical";
  neighborhood: string | null;
  location: string | null;
  anonymous: number;
  admin_notes: string | null;
  reviewer_note: string | null;
  reviewer_note_at: string | null;
  created_at: string;
  updated_at: string;
}

// Member-facing labels for report statuses. The DB enum uses different names
// (submitted/needs_verification/etc.) but the pilot spec asks for these.
export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  submitted: "Pending",
  needs_verification: "Under review",
  more_info_needed: "More info needed",
  approved: "Approved",
  rejected: "Rejected",
  resolved: "Resolved",
};

export interface Organization {
  id: number;
  name: string;
  type: string;
  city: string | null;
  neighborhood: string | null;
  contact_name: string | null;
  safety_email: string | null;
  hours: string | null;
}

export interface AccessRequest {
  id: number;
  full_name: string;
  email: string;
  organization_name: string | null;
  community_role: string | null;
  city: string | null;
  neighborhood: string | null;
  reason: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
}

export interface DashboardSummary {
  active_alerts: number;
  awaiting_verification: number;
  my_reports: number;
  trust_score: number;
}

export const REPORT_TYPES: { value: string; label: string }[] = [
  { value: "suspicious_activity", label: "Suspicious activity" },
  { value: "business_safety", label: "Business safety concern" },
  { value: "school_safety", label: "School safety concern" },
  { value: "church_community_safety", label: "Church / community center concern" },
  { value: "missing_person", label: "Missing person concern" },
  { value: "harassment_threat", label: "Harassment / threat" },
  { value: "fraud_scam", label: "Fraud / scam concern" },
  { value: "community_conflict", label: "Community conflict" },
  { value: "infrastructure_hazard", label: "Infrastructure hazard" },
  { value: "other", label: "Other" },
];

export const URGENCY_LEVELS = ["low", "medium", "high", "critical"] as const;

export const ORG_TYPES: { value: string; label: string }[] = [
  { value: "business", label: "Business" },
  { value: "church", label: "Church" },
  { value: "school", label: "School" },
  { value: "community_center", label: "Community center" },
  { value: "nonprofit", label: "Nonprofit" },
  { value: "neighborhood_group", label: "Neighborhood group" },
  { value: "other", label: "Other" },
];

export function reportTypeLabel(value: string): string {
  return REPORT_TYPES.find((r) => r.value === value)?.label ?? value;
}
